export default class Calendar {
  constructor(inputElement) {
    this.input = inputElement;

    // Config via data-attrs (optional)
    const minYearAttr = parseInt(this.input.getAttribute('data-min-year') || '', 10);
    const maxYearAttr = parseInt(this.input.getAttribute('data-max-year') || '', 10);

    // Locale & number formatting
    this.locale = (document.documentElement.lang || 'en').toLowerCase();
    this.numberFmt = new Intl.NumberFormat(this.locale);

    // Normalised "today" for comparisons
    this.today = new Date();
    this.today.setHours(0, 0, 0, 0);

    // Year bounds (default: current year → current year + 50)
    const todayY = this.today.getFullYear();
    this.minYear = Number.isFinite(minYearAttr) ? Math.max(minYearAttr, todayY) : todayY;
    this.maxYear = Number.isFinite(maxYearAttr) ? Math.max(maxYearAttr, this.minYear) : todayY + 50;

    // Active & selected dates
    this.date = new Date(
      Math.min(Math.max(todayY, this.minYear), this.maxYear),
      this.today.getMonth(),
      1
    );
    this.selectedDate = null;

    // DOM refs we’ll reuse
    this.monthSelect = null;
    this.yearSelect  = null;

    this.calendar = this._createCalendar();
    this._bindEvents();
  }

  /* ---------------- Events ---------------- */

  _bindEvents() {
    this.input.addEventListener('focus', () => this._toggleCalendar());
    window.addEventListener('resize', () => this._closeCalendar());

    document.addEventListener('click', (e) => {
      const path = e.composedPath ? e.composedPath() : [];
      const inside = path.includes(this.calendar) || e.target === this.input;
      if (!inside) this._closeCalendar();
    });
  }

  _toggleCalendar() {
    if (this.calendar.style.display === 'block') {
      this._closeCalendar();
    } else {
      this._openCalendar();
    }
  }

  _openCalendar() {
    this._renderCalendar();
    document.body.appendChild(this.calendar);
    this.calendar.style.display = 'block';
    this._positionCalendar();
  }

  _closeCalendar() {
    this.calendar.style.display = 'none';
  }

  /* ---------------- Render ---------------- */

  _renderCalendar() {
    this.calendar.innerHTML = '';
    const header = this._createCalendarHeader();
    const days   = this._createCalendarDays();
    const footer = this._createFooter();

    this.calendar.appendChild(header);
    this.calendar.appendChild(days);
    this.calendar.appendChild(footer);
  }

  _createCalendarHeader() {
    const header = document.createElement('div');
    header.classList.add('calendar__header');

    const y = this.date.getFullYear();
    const m = this.date.getMonth();
    const todayY = this.today.getFullYear();
    const todayM = this.today.getMonth();

    const monthFmt = new Intl.DateTimeFormat(this.locale, { month: 'long' });

    // Helpers to decide enabled/disabled states (future-only)
    const canPrevMonth = () => !(y < todayY || (y === todayY && m <= todayM)); // block going to past or current month backwards
    const canNextMonth = () => !(y === this.maxYear && m === 11);
    const canPrevYear  = () => y > todayY;                 // don’t go back before current year
    const canNextYear  = () => y < this.maxYear;

    // --- Month controls ---
    const monthGroup = document.createElement('div');
    monthGroup.className = 'calendar__group calendar__group--month';

    const prevMonthBtn = document.createElement('button');
    prevMonthBtn.type = 'button';
    prevMonthBtn.className = 'calendar__btn calendar__btn--prev-month';
    prevMonthBtn.innerHTML = '<i class="icon icon--chevron left"></i>';
    if (!canPrevMonth()) prevMonthBtn.classList.add('is-disabled');
    prevMonthBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (canPrevMonth()) this._changeMonth(-1);
    });

    const monthLabel = document.createElement('span');
    monthLabel.className = 'calendar__label calendar__label--month';
    monthLabel.textContent = monthFmt.format(new Date(2020, m, 1));

    const nextMonthBtn = document.createElement('button');
    nextMonthBtn.type = 'button';
    nextMonthBtn.className = 'calendar__btn calendar__btn--next-month';
    nextMonthBtn.innerHTML = '<i class="icon icon--chevron right"></i>';
    if (!canNextMonth()) nextMonthBtn.classList.add('is-disabled');
    nextMonthBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (canNextMonth()) this._changeMonth(1);
    });

    monthGroup.append(prevMonthBtn, monthLabel, nextMonthBtn);

    // --- Year controls ---
    const yearGroup = document.createElement('div');
    yearGroup.className = 'calendar__group calendar__group--year';

    const prevYearBtn = document.createElement('button');
    prevYearBtn.type = 'button';
    prevYearBtn.className = 'calendar__btn calendar__btn--prev-year';
    prevYearBtn.innerHTML = '<i class="icon icon--chevron left"></i>';
    if (!canPrevYear()) prevYearBtn.classList.add('is-disabled');
    prevYearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!canPrevYear()) return;
      const ny = y - 1;
      this._setYearMonth(ny, m);
    });

    const yearLabel = document.createElement('span');
    yearLabel.className = 'calendar__label calendar__label--year';
    yearLabel.textContent = String(y);

    const nextYearBtn = document.createElement('button');
    nextYearBtn.type = 'button';
    nextYearBtn.className = 'calendar__btn calendar__btn--next-year';
    nextYearBtn.innerHTML = '<i class="icon icon--chevron right"></i>';
    if (!canNextYear()) nextYearBtn.classList.add('is-disabled');
    nextYearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!canNextYear()) return;
      const ny = y + 1;
      this._setYearMonth(ny, m);
    });

    yearGroup.append(prevYearBtn, yearLabel, nextYearBtn);

    header.appendChild(monthGroup);
    header.appendChild(yearGroup);

    return header;
  }

  _updateMonthOptionStates(monthSelect, year) {
    const isThisYear = (year === this.today.getFullYear());
    [...monthSelect.options].forEach((opt) => {
      const m = parseInt(opt.value, 10);
      // In the current year, months before today's month are disabled
      opt.disabled = isThisYear && m < this.today.getMonth();
    });
  }

  _createCalendarDays() {
    const daysWrapper = document.createElement('div');
    daysWrapper.classList.add('calendar__days');

    const y = this.date.getFullYear();
    const m = this.date.getMonth();

    const firstDayOfMonth  = new Date(y, m, 1);
    const lastDayOfMonth   = new Date(y, m + 1, 0);
    const numDaysInMonth   = lastDayOfMonth.getDate();
    const startDay         = firstDayOfMonth.getDay();
    const prevMonthLastDay = new Date(y, m, 0).getDate();

    // Prev month trailing days — always past relative to current grid month
    for (let i = startDay - 1; i >= 0; i--) {
      const d = prevMonthLastDay - i;
      const dayCell = document.createElement('div');
      dayCell.textContent = this._n(d);
      dayCell.classList.add('calendar__day', 'calendar__day--faded', 'calendar__day--disabled');
      dayCell.setAttribute('aria-disabled', 'true');
      daysWrapper.appendChild(dayCell);
    }

    // Current month days
    for (let day = 1; day <= numDaysInMonth; day++) {
      const cellDate = new Date(y, m, day);
      const dayCell = document.createElement('div');
      dayCell.textContent = this._n(day);
      dayCell.classList.add('calendar__day');

      // Today marker
      if (
        cellDate.getDate()  === this.today.getDate() &&
        cellDate.getMonth() === this.today.getMonth() &&
        cellDate.getFullYear() === this.today.getFullYear()
      ) {
        dayCell.classList.add('calendar__day--today');
      }

      // Selected marker
      if (
        this.selectedDate &&
        this.selectedDate.getDate()  === day &&
        this.selectedDate.getMonth() === m &&
        this.selectedDate.getFullYear() === y
      ) {
        dayCell.classList.add('calendar__day--selected');
      }

      if (this._isPast(cellDate)) {
        dayCell.classList.add('calendar__day--disabled');
        dayCell.setAttribute('aria-disabled', 'true');
      } else {
        dayCell.addEventListener('click', () => this._selectDate(day));
      }

      daysWrapper.appendChild(dayCell);
    }

    // Next month leading days (fill to 6 rows → 42 cells)
    const totalCells = daysWrapper.children.length;
    const remainingCells = 42 - totalCells;
    for (let day = 1; day <= remainingCells; day++) {
      const cellDate = new Date(y, m + 1, day);
      const dayCell = document.createElement('div');
      dayCell.textContent = this._n(day);
      dayCell.classList.add('calendar__day', 'calendar__day--faded');

      if (this._isPast(cellDate)) {
        dayCell.classList.add('calendar__day--disabled');
        dayCell.setAttribute('aria-disabled', 'true');
      } else {
        dayCell.addEventListener('click', () => {
          this._changeMonth(1);
          this._selectDate(day);
        });
      }

      daysWrapper.appendChild(dayCell);
    }

    return daysWrapper;
  }

  _createFooter() {
    const footer = document.createElement('div');
    footer.classList.add('calendar__footer');

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.classList.add('calendar__btn', 'calendar__btn--cancel');
    cancelBtn.textContent = (window.localizedData?.cancel) || 'Cancel';
    cancelBtn.addEventListener('click', () => {
      this.selectedDate = null;
      this.input.value = '';
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this._closeCalendar();
    });

    const okBtn = document.createElement('button');
    okBtn.type = 'button';
    okBtn.classList.add('calendar__btn', 'calendar__btn--ok');
    okBtn.textContent = (window.localizedData?.ok) || 'OK';
    okBtn.addEventListener('click', () => this._closeCalendar());

    footer.appendChild(cancelBtn);
    footer.appendChild(okBtn);
    return footer;
  }

  /* ---------------- Behaviour ---------------- */

  _setYearMonth(year, month) {
    if (year < this.minYear) year = this.minYear;
    if (year > this.maxYear) year = this.maxYear;

    // If moving within the current year, don’t allow months before the current month
    if (year === this.today.getFullYear() && month < this.today.getMonth()) {
      month = this.today.getMonth();
    }

    const currentDay = Math.min(this.date.getDate(), new Date(year, month + 1, 0).getDate());
    this.date = new Date(year, month, currentDay);

    // Sync custom selects without firing onChange
    if (this._yearAPI)  { this._yearAPI.native.value  = String(year);  this._yearAPI.paint(); }
    if (this._monthAPI) { this._monthAPI.native.value = String(month); this._monthAPI.paint(); }

    this._renderCalendar();
    this._positionCalendar();
  }

  _changeMonth(direction) {
    const next = new Date(this.date);
    next.setMonth(next.getMonth() + direction);

    const nextKey  = next.getFullYear() * 12 + next.getMonth();
    const minKey   = this.today.getFullYear() * 12 + this.today.getMonth();
    const maxKey   = this.maxYear * 12 + 11;

    if (nextKey < minKey) return; // don’t go into the past
    if (nextKey > maxKey) return; // don’t go past max bounds
    if (next.getFullYear() < this.minYear || next.getFullYear() > this.maxYear) return;

    this.date = next;

    // Sync custom selects without firing onChange
    if (this._yearAPI)  { this._yearAPI.native.value  = String(this.date.getFullYear()); this._yearAPI.paint(); }
    if (this._monthAPI) { this._monthAPI.native.value = String(this.date.getMonth());    this._monthAPI.paint(); }

    this._renderCalendar();
    this._positionCalendar();
  }

  _selectDate(day) {
    const candidate = new Date(this.date.getFullYear(), this.date.getMonth(), day);
    if (this._isPast(candidate)) return;

    this.selectedDate = candidate;
    const formattedDate =
      `${String(this.selectedDate.getDate()).padStart(2, '0')}/` +
      `${String(this.selectedDate.getMonth() + 1).padStart(2, '0')}/` +
      `${this.selectedDate.getFullYear()}`;

    this.input.value = formattedDate;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this._closeCalendar();
  }

  // Past = strictly before today (today is allowed)
  _isPast(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime() < this.today.getTime();
  }

  _n(n) {
    try { return this.numberFmt.format(n); } catch { return String(n); }
  }

  /* ---------------- DOM utils ---------------- */

  _createCalendar() {
    const calendar = document.createElement('div');
    calendar.classList.add('calendar');
    calendar.style.position = 'absolute';
    calendar.style.display = 'none';
    calendar.style.zIndex = 9999;
    return calendar;
  }

  _positionCalendar() {
    const rect = this.input.getBoundingClientRect();
    this.calendar.style.top = `${rect.bottom + window.scrollY + 16}px`;
    this.calendar.style.left = `${rect.left + window.scrollX}px`;
  }
}